import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Home, Users, Monitor, Plus, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function HouseholdOverview() {
  const navigate = useNavigate();
  const [households, setHouseholds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newHouseholdName, setNewHouseholdName] = useState('');

  useEffect(() => {
    fetchHouseholds();
  }, []);

  const fetchHouseholds = async () => {
    setLoading(true);
    try {
      // For now, we'll fetch all households by querying the database
      // In production, you'd have a GET /api/households endpoint
      const response = await axios.get(`${API}/households`);
      setHouseholds(response.data || []);
    } catch (error) {
      console.error('Error fetching households:', error);
      setHouseholds([]);
    } finally {
      setLoading(false);
    }
  };

  const createHousehold = async (e) => {
    e.preventDefault();
    if (!newHouseholdName.trim()) {
      toast.error('Bitte geben Sie einen Haushaltsnamen ein');
      return;
    }

    try {
      await axios.post(`${API}/households`, {
        name: newHouseholdName
      });
      toast.success('Haushalt erfolgreich erstellt!');
      setNewHouseholdName('');
      setShowCreateForm(false);
      fetchHouseholds();
    } catch (error) {
      console.error('Error creating household:', error);
      toast.error('Fehler beim Erstellen des Haushalts');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Lade Haushalte...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      {/* Migration Notice */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-gradient-to-r from-[#5B4B8A] to-[#6DCEE8] text-white rounded-2xl p-6 shadow-xl">
          <h2 className="text-2xl font-bold mb-3">🚀 Neue Admin-Oberfläche verfügbar!</h2>
          <p className="mb-4 opacity-90">
            Diese Ansicht wird durch das neue Admin-Dashboard ersetzt. 
            Nutze die moderne Family-App für alle Admin-Funktionen.
          </p>
          <button
            onClick={() => navigate('/family/admin/settings')}
            className="px-6 py-3 bg-white text-[#5B4B8A] rounded-xl font-semibold hover:bg-neutral-100 transition-colors shadow-lg"
          >
            Zum neuen Admin-Dashboard →
          </button>
        </div>
      </div>
      
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="/logo.svg" 
              alt="Hayat Assistant Logo" 
              className="w-14 h-14 rounded-full shadow-md"
            />
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Hayat Assistant</h1>
              <p className="text-gray-600 mt-1">Haushalts-Übersicht</p>
            </div>
          </div>
          <Button onClick={() => navigate('/dashboard')}>
            Zurück zum alten Dashboard
          </Button>
        </div>
      </div>

      {/* Create Household Button */}
      <div className="max-w-7xl mx-auto mb-6">
        {!showCreateForm && (
          <Button onClick={() => setShowCreateForm(true)} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Neuen Haushalt erstellen
          </Button>
        )}

        {showCreateForm && (
          <Card>
            <CardHeader>
              <CardTitle>Neuen Haushalt erstellen</CardTitle>
              <CardDescription>Geben Sie einen Namen für den neuen Haushalt ein</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={createHousehold} className="space-y-4">
                <div>
                  <Label htmlFor="household-name">Haushaltsname</Label>
                  <Input
                    id="household-name"
                    value={newHouseholdName}
                    onChange={(e) => setNewHouseholdName(e.target.value)}
                    placeholder="z.B. Familie Müller"
                  />
                </div>
                <div className="flex gap-2">
                  <Button type="submit">Erstellen</Button>
                  <Button type="button" variant="outline" onClick={() => {
                    setShowCreateForm(false);
                    setNewHouseholdName('');
                  }}>
                    Abbrechen
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Households Grid */}
      <div className="max-w-7xl mx-auto">
        {households.length === 0 ? (
          <Card>
            <CardContent className="pt-6 text-center">
              <Home className="w-16 h-16 mx-auto text-gray-400 mb-4" />
              <h3 className="text-xl font-semibold mb-2">Keine Haushalte vorhanden</h3>
              <p className="text-gray-600 mb-4">Erstellen Sie Ihren ersten Haushalt, um zu beginnen</p>
              <Button onClick={() => setShowCreateForm(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Haushalt erstellen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {households.map((household) => (
              <Card 
                key={household.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/households/${household.id}`)}
              >
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Home className="w-5 h-5" />
                    {household.name}
                  </CardTitle>
                  <CardDescription>
                    Erstellt: {new Date(household.created_at).toLocaleDateString('de-DE')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1 text-gray-600">
                        <Users className="w-4 h-4" />
                        Senioren
                      </span>
                      <span className="font-semibold">-</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-1 text-gray-600">
                        <Monitor className="w-4 h-4" />
                        Geräte
                      </span>
                      <span className="font-semibold">-</span>
                    </div>
                  </div>
                  <Button className="w-full mt-4" variant="outline">
                    Details ansehen
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
